import React from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { Eyebrow, Pill, Card } from "../components/shared/BrandPrimitives";
import { MachinesNav, MachinesFooter } from "./Machines";

/**
 * Machines side sub-pages. All copy is verbatim from the master copy
 * document. British English, no contractions, no em/en dashes.
 * The only CTA that routes off-page is the AI roadmap pill, which routes
 * to /plan (the audit funnel).
 */

const ROADMAP_LABEL = "Get Your Firm\u2019s Free Customised AI Roadmap";
const ROADMAP_TO = "/plan";

function PageShell({ testId, title, children }) {
  React.useEffect(() => {
    if (title) document.title = title;
  }, [title]);
  return (
    <div
      data-testid={testId}
      style={{
        background: "var(--c-linen)",
        color: "var(--c-forest)",
        minHeight: "100vh",
        fontFamily: "var(--font-sans)",
      }}
    >
      <MachinesNav />
      {children}
      <MachinesFooter />
    </div>
  );
}

function HeroBlock({ eyebrow, headline, subline, testId }) {
  return (
    <section
      data-testid={testId}
      style={{
        padding: "var(--section-py-desktop) var(--gutter-desktop)",
        maxWidth: "var(--container-max)",
        margin: "0 auto",
      }}
    >
      {eyebrow ? <Eyebrow glyph>{eyebrow}</Eyebrow> : null}
      <h1
        style={{
          fontFamily: "var(--font-sans)",
          fontSize: "var(--type-display-size)",
          lineHeight: "var(--type-display-lh)",
          letterSpacing: "-0.022em",
          margin: eyebrow ? "var(--s-4) 0 0" : 0,
          maxWidth: "22ch",
        }}
      >
        {headline}
      </h1>
      {subline ? (
        <p
          style={{
            color: "#3D4A44",
            fontSize: "var(--type-body-size)",
            lineHeight: "var(--type-body-lh)",
            maxWidth: "60ch",
            marginTop: "var(--s-5)",
          }}
        >
          {subline}
        </p>
      ) : null}
      <div style={{ marginTop: "var(--s-6)" }}>
        <Pill as={Link} to={ROADMAP_TO} variant="primary" data-testid={`${testId}-cta`}>
          {ROADMAP_LABEL}
        </Pill>
      </div>
    </section>
  );
}

function CTABand({ line, testId = "machines-page-cta-band" }) {
  return (
    <section
      data-testid={testId}
      style={{
        padding: "var(--statement-py-desktop) var(--gutter-desktop)",
        textAlign: "center",
        background: "var(--c-linen)",
        borderTop: "1px solid rgba(22,30,27,0.12)",
        borderBottom: "1px solid rgba(22,30,27,0.12)",
      }}
    >
      <p
        style={{
          fontFamily: "var(--font-sans)",
          fontSize: "var(--type-h1-size)",
          lineHeight: "var(--type-h1-lh)",
          letterSpacing: "-0.02em",
          maxWidth: "26ch",
          margin: "0 auto var(--s-6)",
        }}
      >
        {line}
      </p>
      <Pill as={Link} to={ROADMAP_TO} variant="primary" data-testid={`${testId}-pill`}>
        {ROADMAP_LABEL}
      </Pill>
    </section>
  );
}

/* ============================================================================
   /machines/products
   ============================================================================ */

const PRODUCTS = [
  {
    key: "autonomy",
    name: "Machine Autonomy",
    heading: "The Machine Learns Its Job.",
    body:
      "Machine Autonomy is our end to end stack for machines that move, from assisted operation to full autonomy. It is built for the real conditions of working sites: dust, weather, degraded networks and mixed fleets of old and new equipment. Operators supervise from the seat or from the control room, and every level of autonomy is a choice the owner makes, not a switch a vendor flips.",
  },
  {
    key: "os",
    name: "Machine OS",
    heading: "One Operating Layer for Every Machine You Own.",
    body:
      "Machine OS unifies the software life of a fleet: onboard runtime, updates, telemetry, integrations and coordination between machines. It is designed for mixed fleets across manufacturers and generations, so a ten year old hauler and a new loader speak the same language. Deployed on site, updated on your schedule, governed by your rules.",
  },
  {
    key: "data-engine",
    name: "Machine Data Engine",
    heading: "Every Hour of Work Makes the Fleet Smarter.",
    body:
      "The Machine Data Engine turns operating data into improvement. It collects, curates and labels what the fleet experiences, feeds simulation and validation, and returns better models to the machines. The loop runs on infrastructure the owner controls, so the data a site generates builds an asset for that site, not a moat for a supplier.",
  },
  {
    key: "operator-intelligence",
    name: "Operator Intelligence",
    heading: "People Stay in Command.",
    body:
      "Operator Intelligence is the human side of the platform: on machine interfaces, remote supervision and control room views that let one team direct many machines with confidence. It is built on a simple belief. Autonomy should promote operators, not replace them, and the person responsible for a site should always be able to see, question and override what a machine intends to do.",
  },
];

export function MachinesProducts() {
  return (
    <PageShell testId="machines-products-root" title="Products | Crowther for Machines">
      <HeroBlock
        eyebrow="PRODUCTS"
        headline="Physical AI, Owned by the People Who Run It."
        subline="Machines are bought to work for decades. The intelligence inside them should serve the owner just as long. Every Crowther product below is built on one rule: it runs where you run, it learns from your work, and it obeys your keys."
        testId="machines-products-hero"
      />

      <section
        style={{
          maxWidth: "var(--container-max)",
          margin: "0 auto",
          padding: "0 var(--gutter-desktop) var(--section-py-desktop)",
        }}
      >
        {PRODUCTS.map((p, i) => (
          <div
            key={p.key}
            data-testid={`machines-product-${p.key}`}
            style={{
              paddingTop: "var(--s-9)",
              paddingBottom: "var(--s-9)",
              borderTop: i === 0 ? "1px solid rgba(22,30,27,0.12)" : "none",
              borderBottom: "1px solid rgba(22,30,27,0.12)",
            }}
          >
            <div
              style={{
                fontFamily: "var(--font-sans)",
                fontSize: 12,
                letterSpacing: "0.14em",
                textTransform: "uppercase",
                color: "var(--c-emerald)",
                marginBottom: "var(--s-3)",
              }}
            >
              {p.name}
            </div>
            <h2
              style={{
                fontFamily: "var(--font-sans)",
                fontSize: "var(--type-h2-size)",
                lineHeight: "var(--type-h2-lh)",
                letterSpacing: "-0.02em",
                margin: 0,
                maxWidth: "22ch",
              }}
            >
              {p.heading}
            </h2>
            <p
              style={{
                color: "#3D4A44",
                marginTop: "var(--s-4)",
                maxWidth: "68ch",
                fontSize: "var(--type-body-size)",
                lineHeight: "var(--type-body-lh)",
              }}
            >
              {p.body}
            </p>
          </div>
        ))}
      </section>

      <CTABand line="Learn more about Crowther for machines." testId="machines-products-cta" />
    </PageShell>
  );
}

/* ============================================================================
   /machines/approach
   ============================================================================ */

const APPROACH_SECTIONS = [
  {
    title: "Evidence Before Autonomy",
    body:
      "Every capability we ship is earned in simulation, proven in controlled operation and only then trusted with real work. Autonomy is not a launch event. It is a burden of proof, carried in stages, with the owner deciding when each stage has been met.",
  },
  {
    title: "The Owner Holds the Keys",
    body:
      "Our systems are governed by a rule we apply everywhere: deny by default. Access to a machine, its data and its behaviour is granted explicitly by the owner and can be revoked in a moment. We build the locks, and we hand over the keys.",
  },
  {
    title: "On Site Is the Default",
    body:
      "Working sites lose connectivity, and critical operations cannot inherit someone else\u2019s outage. Intelligence therefore lives on the machine and on site infrastructure, with the cloud as an option rather than a dependency.",
  },
  {
    title: "People Are Promoted, Not Removed",
    body:
      "The point of machine intelligence is to move people from the dangerous, repetitive centre of the work to its supervising edge. Operators become directors of fleets. Their judgement is the part of the system we refuse to automate away.",
  },
];

export function MachinesApproach() {
  return (
    <PageShell testId="machines-approach-root" title="Approach | Crowther for Machines">
      <HeroBlock
        eyebrow="APPROACH"
        headline="Machines That Cannot Fail Quietly."
        subline="A chatbot that errs produces a bad paragraph. A machine that errs produces a bad day, or far worse. Everything about how Crowther builds machine intelligence follows from taking that difference seriously."
        testId="machines-approach-hero"
      />

      <section
        style={{
          maxWidth: "var(--container-max)",
          margin: "0 auto",
          padding: "0 var(--gutter-desktop) var(--section-py-desktop)",
        }}
      >
        {APPROACH_SECTIONS.map((s, i) => (
          <div
            key={s.title}
            data-testid={`machines-approach-section-${i + 1}`}
            style={{
              paddingTop: "var(--s-9)",
              paddingBottom: "var(--s-9)",
              borderTop: "1px solid rgba(22,30,27,0.12)",
            }}
          >
            <div
              style={{
                fontFamily: "var(--font-sans)",
                fontSize: 12,
                letterSpacing: "0.14em",
                color: "#3D4A44",
              }}
            >
              Section {String(i + 1).padStart(2, "0")}
            </div>
            <h2
              style={{
                fontFamily: "var(--font-sans)",
                fontSize: "var(--type-h2-size)",
                lineHeight: "var(--type-h2-lh)",
                letterSpacing: "-0.02em",
                margin: "var(--s-3) 0 0",
                maxWidth: "22ch",
              }}
            >
              {s.title}
            </h2>
            <p
              style={{
                color: "#3D4A44",
                marginTop: "var(--s-4)",
                maxWidth: "70ch",
                fontSize: "var(--type-body-size)",
                lineHeight: "var(--type-body-lh)",
              }}
            >
              {s.body}
            </p>
          </div>
        ))}

        <p
          style={{
            fontFamily: "var(--font-sans)",
            fontSize: "var(--type-h2-size)",
            lineHeight: "var(--type-h2-lh)",
            letterSpacing: "-0.02em",
            marginTop: "var(--s-9)",
            maxWidth: "28ch",
          }}
        >
          This is slower than promising everything at once. It is also how machines earn trust.
        </p>
        <div style={{ marginTop: "var(--s-6)" }}>
          <Pill as={Link} to={ROADMAP_TO} variant="primary" data-testid="machines-approach-close-cta">
            {ROADMAP_LABEL}
          </Pill>
        </div>
      </section>
    </PageShell>
  );
}

/* ============================================================================
   Industries — data-driven pages
   ============================================================================ */

const INDUSTRIES = {
  manufacturing: {
    slug: "manufacturing",
    title: "Manufacturing",
    heroHeadline: "The Factory Floor Is Learning to Run Itself.",
    heroSubline: "Intelligence for the lines, cells and machines that make everything else.",
    positioning:
      "A factory is a promise of repeatability. Intelligence keeps that promise at higher speed, with fewer surprises, and without handing the plant to an outside cloud.",
    products: [
      { name: "Machine Autonomy", body: "Autonomy for the moving machines of the plant, from tugs and transfer vehicles to automated cells, tuned for tight spaces and constant human presence." },
      { name: "Machine OS", body: "One software layer across lines and generations of equipment, so upgrades happen in software before they demand new steel." },
      { name: "Machine Data Engine", body: "Every cycle, fault and recovery becomes training data. The plant that runs also teaches." },
      { name: "Operator Intelligence", body: "Supervisors see the whole line, intervene in seconds and keep authority over every automated decision." },
    ],
    advantages: [
      { name: "Uptime Is the Product", body: "Intelligence that predicts, adapts and recovers keeps lines moving through variation that would once have stopped them." },
      { name: "Old Equipment, New Behaviour", body: "Software defined capability reaches machines that were never designed for it, extending the working life of capital already paid for." },
      { name: "The Plant Keeps Its Secrets", body: "Process data is competitive advantage. It stays on site, on infrastructure the manufacturer owns." },
    ],
    faq: [
      { q: "What does it mean to make a factory machine intelligent?", a: "It means giving the machine the ability to perceive its situation, adapt its behaviour within set limits and report honestly on what it did. Intelligence is added in software, under rules the plant defines." },
      { q: "Does machine intelligence replace factory workers?", a: "It changes work more than it removes it. Machines take the repetitive and hazardous portions, and people move to supervision, exception handling and improvement, roles that decide whether a plant actually gets better." },
      { q: "Can intelligence be added to existing production equipment?", a: "Often, yes. Retrofit is a software and sensing question before it is a hardware question, and it is usually the fastest route to value in an existing plant." },
    ],
    ctaLine: "Learn more about Crowther for manufacturing.",
  },
  logistics: {
    slug: "logistics",
    title: "Logistics & Warehousing",
    heroHeadline: "Every Shelf, Tote and Trailer, Accounted For.",
    heroSubline: "Intelligence for the machines that store, pick and move the world\u2019s goods.",
    positioning:
      "A warehouse is a flow problem that never sleeps. Intelligent machines give it a memory, a plan and the discipline to keep both.",
    products: [
      { name: "Machine Autonomy", body: "Autonomy for mobile robots, tugs and yard vehicles that share floors with people, built for dense traffic and constant change." },
      { name: "Machine OS", body: "Coordinates mixed fleets from different manufacturers as one system with one set of rules." },
      { name: "Machine Data Engine", body: "Peak seasons, misspicks and near misses become the training set for a smarter operation." },
      { name: "Operator Intelligence", body: "One control room view of every moving machine, with authority to redirect any of them at any moment." },
    ],
    advantages: [
      { name: "Throughput Without Chaos", body: "Fleets that plan together move more with fewer conflicts, and keep moving when the plan changes mid shift." },
      { name: "One System, Many Vendors", body: "Intelligence that sits above the equipment brands, so the operation is never hostage to any one of them." },
      { name: "Your Flow Data Stays Yours", body: "What moves through your buildings describes your business. It is not a dataset for someone else to sell." },
    ],
    faq: [
      { q: "What is fleet orchestration in a warehouse?", a: "It is the coordination of many autonomous machines as one system: shared maps, shared priorities and traffic rules, so machines cooperate instead of competing for the same aisle." },
      { q: "How do autonomous warehouse machines work safely around people?", a: "Through layered perception, conservative behaviour near people, strict speed and distance rules, and the ability for any supervisor to stop or redirect a machine instantly." },
      { q: "Can one platform manage robots from different manufacturers?", a: "That is the point of an operating layer. It abstracts the differences between vendors so the operation sets the rules once and every machine follows them." },
    ],
    ctaLine: "Learn more about Crowther for logistics.",
  },
  agriculture: {
    slug: "agriculture",
    title: "Agriculture",
    heroHeadline: "Intelligence That Grows With the Season.",
    heroSubline: "For the tractors, harvesters and field machines that feed the world.",
    positioning:
      "Farming rewards precision and punishes waste. Machine intelligence brings both lessons to every pass over every field.",
    products: [
      { name: "Machine Autonomy", body: "Autonomy for tractors, harvesters and sprayers, built for open fields, variable terrain and work that cannot wait for perfect weather." },
      { name: "Machine OS", body: "One platform across the shed, from the newest harvester to the oldest reliable tractor, updated between seasons, not during them." },
      { name: "Machine Data Engine", body: "Every season becomes evidence. Planting, spraying and harvest data feed models that make the next season more precise." },
      { name: "Operator Intelligence", body: "Interfaces that respect how farmers actually work, in the cab, on a phone at the fence line, or reviewing the day at the kitchen table." },
    ],
    advantages: [
      { name: "Precision Per Acre", body: "Machines that hold their line, meter their inputs and log what they did, hour after hour, without drift." },
      { name: "Built for Distance", body: "Farms are far from data centres. Intelligence runs on the machine and the farm office, and does not stop when coverage does." },
      { name: "The Farm\u2019s Data Belongs to the Farm", body: "Yield maps and field history are the farm\u2019s inheritance. They are stored and governed on the owner\u2019s terms." },
    ],
    faq: [
      { q: "What can autonomous farm machines do today?", a: "Guidance, repetitive field operations and supervised autonomous passes are practical now. The path runs from assistance to supervision to autonomy, at the pace the operation chooses." },
      { q: "Does farm machine intelligence need constant connectivity?", a: "No. It is designed to work with intermittent connectivity, doing its thinking on the machine and synchronising when a connection is available." },
      { q: "Who owns the data an intelligent farm machine collects?", a: "On this platform, the owner of the machine. Field data stays under the farm\u2019s control and is shared only when the farm decides it should be." },
    ],
    ctaLine: "Learn more about Crowther for agriculture.",
  },
  mining: {
    slug: "mining",
    title: "Mining & Quarrying",
    heroHeadline: "The Hardest Ground Demands the Steadiest Machines.",
    heroSubline: "Intelligence for haulage, loading and the operations around them.",
    positioning:
      "A mine measures everything in tonnes and hours. Intelligent machines protect both, and the people who work among them.",
    products: [
      { name: "Machine Autonomy", body: "Autonomy for haul trucks, loaders and support vehicles, engineered for dust, gradient, vibration and the discipline of a working pit." },
      { name: "Machine OS", body: "One operating layer across the fleet, so haulage, loading and support move to one plan." },
      { name: "Machine Data Engine", body: "Every cycle hauled feeds models of the pit that make the next cycle safer and faster." },
      { name: "Operator Intelligence", body: "Control room supervision of the whole operation, with the authority to hold, redirect or stop any machine on site." },
    ],
    advantages: [
      { name: "Safety by Separation", body: "The surest way to protect people from heavy machines is distance. Autonomy puts operators in the control room, not the blast radius." },
      { name: "Utilisation Around the Clock", body: "Machines that do not fatigue extend the working day to its physical limits, shift after shift." },
      { name: "Sovereign by Site", body: "A mine is often remote by definition. The intelligence that runs it lives on site, works offline and answers to the operation." },
    ],
    faq: [
      { q: "What is autonomous haulage?", a: "The operation of haul trucks on defined routes without an onboard driver, supervised from a control room, with layered safety systems governing speed, spacing and right of way." },
      { q: "How does autonomy improve mine safety?", a: "Chiefly by removing people from the areas of highest risk, and secondarily by replacing variable human performance in fatiguing tasks with consistent machine behaviour." },
      { q: "Can autonomy work in a mine with no reliable network?", a: "It must. Machines carry their own intelligence and coordinate through site infrastructure, so the operation does not depend on a distant cloud." },
    ],
    ctaLine: "Learn more about Crowther for mining.",
  },
  freight: {
    slug: "freight",
    title: "Freight & Haulage",
    heroHeadline: "The Goods Must Move. The Driver Should Not Have to Suffer for It.",
    heroSubline: "Intelligence for trucks, from driver assistance to autonomous corridors.",
    positioning:
      "Freight is a promise with a deadline. Intelligent trucks keep the promise more often, more safely and at lower cost per kilometre.",
    products: [
      { name: "Machine Autonomy", body: "A staged path for fleets, from advanced assistance that protects drivers today to autonomous operation on defined corridors tomorrow." },
      { name: "Machine OS", body: "The software backbone of a modern fleet: updates, telemetry, integration with dispatch and maintenance, one platform across brands of truck." },
      { name: "Machine Data Engine", body: "Every kilometre driven becomes evidence for validation, and every hard edge case becomes a test the fleet passes before it meets the road again." },
      { name: "Operator Intelligence", body: "Drivers and dispatchers see what the truck sees, and remote supervisors manage autonomous segments with full authority." },
    ],
    advantages: [
      { name: "Safety That Compounds", body: "Assistance systems reduce incidents now, and the data they generate accelerates the safe path to autonomy." },
      { name: "A Fleet, Not a Collection of Trucks", body: "One operating layer across the fleet turns individual vehicles into a coordinated operation." },
      { name: "Your Routes, Your Rules, Your Data", body: "Operating data from your lanes stays with your business and strengthens your margins, not a platform\u2019s." },
    ],
    faq: [
      { q: "What is the realistic path to autonomous trucking?", a: "Staged. Assistance and safety systems first, then supervised autonomy on defined routes, then expansion as evidence accumulates. Each stage pays for itself." },
      { q: "What happens when an autonomous truck meets a situation it does not understand?", a: "It does the boring, correct thing: slows, holds a safe state and asks for help from a remote supervisor. Composure, not heroics, is the design goal." },
      { q: "Does fleet intelligence work across truck brands?", a: "Yes. The operating layer is built to sit above manufacturers, because almost no real fleet is single brand." },
    ],
    ctaLine: "Learn more about Crowther for freight.",
  },
  ports: {
    slug: "ports",
    title: "Ports & Terminals",
    heroHeadline: "Where Everything the World Trades Changes Hands.",
    heroSubline: "Intelligence for cranes, carriers and the choreography between them.",
    positioning:
      "A terminal is choreography at the scale of thousands of tonnes. Intelligence keeps the dance in time, all day, in all weather.",
    products: [
      { name: "Machine Autonomy", body: "Autonomy for horizontal transport and yard equipment, built for dense, mixed traffic and metal that must never meet metal." },
      { name: "Machine OS", body: "One coordination layer across cranes, carriers and gates, so the terminal moves to a single plan." },
      { name: "Machine Data Engine", body: "Every lift and move refines the model of the yard, and every exception becomes a rehearsed scenario." },
      { name: "Operator Intelligence", body: "A control room view of the whole terminal, with human authority over every automated machine in it." },
    ],
    advantages: [
      { name: "Rhythm Under Pressure", body: "Coordinated machines hold terminal rhythm through surges that break manually scheduled operations." },
      { name: "Precision in Centimetres", body: "Container handling forgives nothing. Machine intelligence holds tolerances people cannot, hour after hour." },
      { name: "Critical Infrastructure, Sovereign Software", body: "Ports are national infrastructure. The software that runs them should answer to the terminal, on infrastructure the terminal controls." },
    ],
    faq: [
      { q: "What parts of a terminal can be automated today?", a: "Horizontal transport, yard cranes and gate processes are the established candidates. The gains compound when one system coordinates them rather than automating each in isolation." },
      { q: "How do automated terminals handle safety?", a: "Through strict segregation of automated zones, layered perception, conservative motion rules and human supervisors with immediate override authority." },
      { q: "Why does software sovereignty matter for a port?", a: "Because a port cannot accept that its operations depend on infrastructure, updates or permissions controlled from outside its jurisdiction. Sovereignty is an operational requirement, not a preference." },
    ],
    ctaLine: "Learn more about Crowther for ports.",
  },
};

export function MachinesIndustries() {
  const list = Object.values(INDUSTRIES);
  return (
    <PageShell testId="machines-industries-root" title="Industries | Crowther for Machines">
      <HeroBlock
        eyebrow="INDUSTRIES"
        headline="Machine Intelligence Where the Work Actually Happens."
        subline="Six sectors where physical AI compounds value hour after hour, shift after shift, season after season."
        testId="machines-industries-hero"
      />

      <section
        style={{
          maxWidth: "var(--container-max)",
          margin: "0 auto",
          padding: "0 var(--gutter-desktop) var(--section-py-desktop)",
          display: "grid",
          gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))",
          gap: "var(--s-5)",
        }}
      >
        {list.map((ind) => (
          <Link
            key={ind.slug}
            to={`/machines/industries/${ind.slug}`}
            data-testid={`machines-industry-card-${ind.slug}`}
            style={{ textDecoration: "none", color: "inherit" }}
          >
            <Card tone="light" padded>
              <div
                style={{
                  fontFamily: "var(--font-sans)",
                  fontSize: 12,
                  letterSpacing: "0.14em",
                  textTransform: "uppercase",
                  color: "var(--c-emerald)",
                  marginBottom: "var(--s-3)",
                }}
              >
                {ind.title}
              </div>
              <h3
                style={{
                  fontFamily: "var(--font-sans)",
                  fontSize: "var(--type-h3-size)",
                  lineHeight: "var(--type-h3-lh)",
                  letterSpacing: "-0.015em",
                  margin: 0,
                }}
              >
                {ind.heroHeadline}
              </h3>
              <p
                style={{
                  color: "#3D4A44",
                  marginTop: "var(--s-3)",
                  fontSize: "var(--type-body-size)",
                  lineHeight: "var(--type-body-lh)",
                }}
              >
                {ind.heroSubline}
              </p>
              <div
                style={{
                  marginTop: "var(--s-4)",
                  color: "var(--c-emerald)",
                  fontSize: "var(--type-body-size)",
                }}
              >
                Read the sector view &rarr;
              </div>
            </Card>
          </Link>
        ))}
      </section>

      <CTABand line="Learn more about Crowther for machines." testId="machines-industries-cta" />
    </PageShell>
  );
}

export function MachinesIndustry() {
  const { slug } = useParams();
  const ind = INDUSTRIES[slug];
  if (!ind) return <Navigate to="/machines/industries" replace />;

  return (
    <PageShell
      testId={`machines-industry-root-${ind.slug}`}
      title={`${ind.title} | Crowther for Machines`}
    >
      <HeroBlock
        eyebrow={ind.title.toUpperCase()}
        headline={ind.heroHeadline}
        subline={ind.heroSubline}
        testId={`machines-industry-hero-${ind.slug}`}
      />

      {/* Positioning statement */}
      <section
        style={{
          padding: "var(--statement-py-desktop) var(--gutter-desktop)",
          textAlign: "center",
        }}
      >
        <p
          style={{
            fontFamily: "var(--font-sans)",
            fontSize: "var(--type-h1-size)",
            lineHeight: "var(--type-h1-lh)",
            letterSpacing: "-0.02em",
            maxWidth: "36ch",
            margin: "0 auto",
          }}
        >
          {ind.positioning}
        </p>
      </section>

      {/* Four products through the industry lens */}
      <section
        data-testid={`machines-industry-products-${ind.slug}`}
        style={{
          maxWidth: "var(--container-max)",
          margin: "0 auto",
          padding: "0 var(--gutter-desktop) var(--section-py-desktop)",
        }}
      >
        <Eyebrow glyph>PRODUCTS FOR THIS INDUSTRY</Eyebrow>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
            gap: "var(--s-5)",
            marginTop: "var(--s-6)",
          }}
        >
          {ind.products.map((p) => (
            <Card key={p.name} tone="light" padded>
              <div
                style={{
                  fontFamily: "var(--font-sans)",
                  fontSize: 12,
                  letterSpacing: "0.14em",
                  textTransform: "uppercase",
                  color: "var(--c-emerald)",
                  marginBottom: "var(--s-3)",
                }}
              >
                {p.name}
              </div>
              <p
                style={{
                  color: "#3D4A44",
                  margin: 0,
                  fontSize: "var(--type-body-size)",
                  lineHeight: "var(--type-body-lh)",
                }}
              >
                {p.body}
              </p>
            </Card>
          ))}
        </div>
      </section>

      {/* Three advantages */}
      <section
        data-testid={`machines-industry-advantages-${ind.slug}`}
        style={{
          maxWidth: "var(--container-max)",
          margin: "0 auto",
          padding: "0 var(--gutter-desktop) var(--section-py-desktop)",
        }}
      >
        <Eyebrow glyph>ADVANTAGES</Eyebrow>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(3, 1fr)",
            gap: "var(--s-5)",
            marginTop: "var(--s-6)",
          }}
        >
          {ind.advantages.map((a) => (
            <Card key={a.name} tone="light" padded>
              <h3
                style={{
                  fontFamily: "var(--font-sans)",
                  fontSize: "var(--type-h3-size)",
                  lineHeight: "var(--type-h3-lh)",
                  letterSpacing: "-0.015em",
                  margin: 0,
                }}
              >
                {a.name}
              </h3>
              <p
                style={{
                  color: "#3D4A44",
                  marginTop: "var(--s-3)",
                  fontSize: "var(--type-body-size)",
                  lineHeight: "var(--type-body-lh)",
                }}
              >
                {a.body}
              </p>
            </Card>
          ))}
        </div>
      </section>

      {/* FAQ */}
      <section
        data-testid={`machines-industry-faq-${ind.slug}`}
        style={{
          maxWidth: "var(--container-max)",
          margin: "0 auto",
          padding: "0 var(--gutter-desktop) var(--section-py-desktop)",
        }}
      >
        <Eyebrow glyph>QUESTIONS OWNERS ASK</Eyebrow>
        <div style={{ marginTop: "var(--s-6)" }}>
          {ind.faq.map((f, i) => (
            <details
              key={i}
              data-testid={`machines-industry-faq-${ind.slug}-${i + 1}`}
              style={{
                borderTop: "1px solid rgba(22,30,27,0.12)",
                padding: "var(--s-5) 0",
              }}
            >
              <summary
                style={{
                  fontFamily: "var(--font-sans)",
                  fontSize: "var(--type-h3-size)",
                  lineHeight: "var(--type-h3-lh)",
                  letterSpacing: "-0.015em",
                  cursor: "pointer",
                  listStyle: "none",
                }}
              >
                {f.q}
              </summary>
              <p
                style={{
                  color: "#3D4A44",
                  marginTop: "var(--s-3)",
                  maxWidth: "68ch",
                  fontSize: "var(--type-body-size)",
                  lineHeight: "var(--type-body-lh)",
                }}
              >
                {f.a}
              </p>
            </details>
          ))}
        </div>
      </section>

      <CTABand line={ind.ctaLine} testId={`machines-industry-cta-${ind.slug}`} />
    </PageShell>
  );
}
